import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { FormGroup } from 'reactstrap';

import { FormField, FIELD_TYPES, FORM_TYPES } from '@techchange/components';
import { messages } from '../../i18n';

const propTypes = {

	/**
	 * The resource to display.
	 */
	resource: PropTypes.shape({

		/**
		 * The unique ID for the resource.
		 */
		id: PropTypes.string,

		/**
		 * The type of the resource.
		 */
		type: PropTypes.string,

		/**
		 * The open date for the resource.
		 */
		open_date: PropTypes.string,

		/**
		 * The ID for the default child resource to display.
		 */
		default_child_id: PropTypes.string,

		/**
		 * Flag indicating if the title should be shown.
		 */
		show_title: PropTypes.bool,

		/**
		 * The metadata for the resource.
		 */
		data: PropTypes.shape({

			/**
			 * The associated form ID (if any) for the resource.
			 */
			form_id: PropTypes.string,

		}),

		/**
		 * The list of children resources (if any).
		 */
		children: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The unique ID for the child resource.
			 */
			id: PropTypes.string,

		})),

	}),

	/**
	 * The form data.
	 */
	data: PropTypes.shape({

		/**
		 * Flag indicating if the title should be shown.
		 */
		show_title: PropTypes.bool,

		/**
		 * True, if learners are allowed to edit form entry submissions.
		 */
		allow_editing_submissions: PropTypes.bool,

		/**
		 * True, if learners are allowed to submit multiple form entries.
		 */
		allow_multiple_submissions: PropTypes.bool,

		/**
		 * The percentage grade needed to pass the quiz (if any).
		 */
		quiz_result_pass_percentage: PropTypes.number,

		/**
		 * True, if learners are allowed to see the correct answers after their submission.
		 */
		quiz_result_show_correct_answers: PropTypes.bool,

	}),

	/**
	 * Function to handle changes to the form data.
	 */
	onInputChange: PropTypes.func.isRequired,

};

const ContentSettingsEditor = (props) => {

	const { resource, data, onInputChange } = props;

	const intl = useIntl();

	return (
		<Fragment>
			<FormGroup>
				<FormField
					id={`resource-${resource.id}-settings-field-show-title`}
					type={FIELD_TYPES.CHECKBOX}
					name="show_title"
					label={intl.formatMessage(
						messages.TC_COMPONENTS_RESOURCE_SETTINGS_MODAL_FIELD_LABEL_TEXT_SHOW_TITLE
					)}
					value={data.show_title}
					isEditing
					isLabelInline
					onChange={onInputChange} />
				{(resource.type === FORM_TYPES.FORM || resource.type === FORM_TYPES.POLL) &&
					<FormField
						id={`resource-${resource.id}-settings-field-allow-editing-submissions`}
						type={FIELD_TYPES.CHECKBOX}
						name="allow_editing_submissions"
						label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_SETTINGS_MODAL_FIELD_LABEL_TEXT_ALLOW_EDITING_SUBMISSIONS)}
						value={data.allow_editing_submissions}
						isEditing
						isLabelInline
						onChange={onInputChange} />
				}
				{(resource.type === FORM_TYPES.FORM || resource.type === FORM_TYPES.QUIZ) &&
					<FormField
						id={`resource-${resource.id}-settings-field-allow-multiple-submissions`}
						type={FIELD_TYPES.CHECKBOX}
						name="allow_multiple_submissions"
						label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_SETTINGS_MODAL_FIELD_LABEL_TEXT_ALLOW_MULTIPLE_SUBMISSIONS)}
						value={data.allow_multiple_submissions}
						isEditing
						isLabelInline
						onChange={onInputChange} />
				}
				{resource.type === FORM_TYPES.QUIZ &&
					<FormField
						id={`resource-${resource.id}-settings-field-show-correct-answers`}
						type={FIELD_TYPES.CHECKBOX}
						name="quiz_result_show_correct_answers"
						label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_SETTINGS_MODAL_FIELD_LABEL_TEXT_QUIZ_SHOW_CORRECT_ANSWERS)}
						value={data.quiz_result_show_correct_answers}
						isEditing
						isLabelInline
						onChange={onInputChange} />
				}
			</FormGroup>
			{resource.type === FORM_TYPES.QUIZ &&
				<FormField
					type={FIELD_TYPES.PERCENTAGE}
					name="quiz_result_pass_percentage"
					value={data.quiz_result_pass_percentage}
					label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_SETTINGS_MODAL_FIELD_LABEL_TEXT_QUIZ_PASS_PERCENTAGE)}
					isEditing
					onChange={onInputChange} />
			}
		</Fragment>
	);

};

ContentSettingsEditor.propTypes = propTypes;

export default ContentSettingsEditor;
